import { STRINGS } from '@/strings/strings';
import { asCitationsString } from '@/features/story/types';

interface Props {
  wowFact: string | null;
  tryThis: string | null;
  parentBox: string | null;
  citations: unknown;
}

/**
 * SB "Kurz erklärt" world-specific block layout (PWA #16 Sub-Phase
 * 16.3 Phase 4d). Three boxes nested inside the Story-Card after the
 * body text, with D1 responsive layout:
 *   1. Wow-Fact   — `bg-sb-wow-fact-bg` (#C9E4FF Sky-Blue +40%)
 *   2. Probier mal — `bg-sb-probier-mal-bg` (#FFF1B9 Gold +40%)
 *   3. Eltern-Info — `bg-dream-navy/5` + Sönke icon (shared
 *      ParentInfoBox component, also used by SB Sachgeschichte)
 *
 * Mobile (< 768px): all 3 stacked vertically.
 * Tablet+ (md: ≥ 768px): Wow + Probier side-by-side, Eltern-Info
 * full-width below.
 *
 * Each box hides itself if its source field is empty/null. No borders
 * (per PWA #16 Cluster B / Sebastian Gate-1 2026-04-29 — bgs are
 * light enough to differentiate against the bg-dream-light-blue card
 * surface without border definition).
 */
export function StorySBKurzerklaert({ wowFact, tryThis, parentBox, citations }: Props) {
  const copy = STRINGS.storyOutput.page;
  const wow = wowFact?.trim() ?? '';
  const tip = tryThis?.trim() ?? '';
  const hasWowOrTip = wow !== '' || tip !== '';

  return (
    <div className="space-y-3">
      {hasWowOrTip && (
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          {wow && (
            <div className="flex-1 rounded-lg bg-sb-wow-fact-bg px-4 py-3">
              <p className="text-sm font-semibold text-dream-navy">{copy.sbWowFact.heading}</p>
              <p className="mt-1 text-sm leading-relaxed text-dream-navy">{wow}</p>
            </div>
          )}
          {tip && (
            <div className="flex-1 rounded-lg bg-sb-probier-mal-bg px-4 py-3">
              <p className="text-sm font-semibold text-dream-navy">{copy.sbTryThis.heading}</p>
              <p className="mt-1 text-sm leading-relaxed text-dream-navy">{tip}</p>
            </div>
          )}
        </div>
      )}
      <ParentInfoBox parentBox={parentBox} citations={citations} />
    </div>
  );
}

interface ParentInfoProps {
  parentBox: string | null;
  citations: unknown;
}

export function ParentInfoBox({ parentBox, citations }: ParentInfoProps) {
  const body = parentBox?.trim() ?? '';
  if (!body) return null;

  const copy = STRINGS.storyOutput.page.sbParentBox;
  const sources = asCitationsString(citations);

  return (
    <div className="rounded-lg border border-dream-navy/10 bg-dream-navy/5 px-4 py-3">
      <div className="flex items-center gap-2">
        <img
          src="/icons/eltern-info-icon.svg"
          alt=""
          aria-hidden="true"
          className="h-5 w-auto shrink-0"
        />
        <p className="text-sm font-semibold text-dream-navy">{copy.heading}</p>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-dream-navy">{body}</p>
      {sources && (
        <div className="mt-3 border-t border-dream-navy/15 pt-2">
          <p className="text-xs text-dream-navy">
            <span className="font-medium">{copy.sourcesLabel}</span> <span>{sources}</span>
          </p>
        </div>
      )}
    </div>
  );
}
